import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import React, { useState, useEffect } from "react";
import {
  Avatar,
  Button,
  Card,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import MainNavigation from "@/components/MainNavigation/MainNavigation";
import PopUpDataGrid from "@/components/DataGrid/PopUpDataGrid";
import { useDispatch } from "react-redux";
import { logOut } from "@/features/auth/authSlice";
import useShepDialogs from "@/components/GenericComponents/ShepDialog/useShepDialogs";
import axios from "@/features/axiosHelper";
import ManageUsers from "./ManageUsers";
import "@/components/Profile/Profile.css";
import { MenuItemEnum } from "@/utils/utils";
import {
  ShepCheckbox,
  ShepButton,
} from "@/components/GenericComponents/ShepComponents";

const columns = [
  {
    field: "UserName",
    headerName: "User Name",
    width: 250,
  },
  {
    field: "IPAddressInformation",
    headerName: "IP Address",
    width: 140,
  },
  {
    field: "LoginTime",
    headerName: "Date / Time",
    type: "datetime",
    width: 200,
    flex: 1,
    valueFormatter: (params) => {
      return dayjs(params.value).utc(true).local().format("YYYY.MM.DD HH:mm");
    },
  },
  {
    field: "AccessDetail",
    headerName: "Notification",
    width: 160,
    flex: 1,
  },
];

const ChangePasswordDialog = ({ open, onClose }) => {
  const { showAlert } = useShepDialogs();
  const [currPassword, setCurrPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [isCurrPassError, setIsCurrPassError] = useState(false);
  const [isConfirmPassError, setIsConfirmPassError] = useState(false);
  const [isNewPassError, setIsNewPassError] = useState(false);
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(false);

  const handleChangePasswordSubmit = (e) => {
    e.preventDefault();

    if (newPassword !== confirmNewPassword) {
      setIsConfirmPassError(true);
      return;
    }

    if (currPassword.trim() === "") {
      setIsCurrPassError(true);
      return;
    }

    if (newPassword.trim() === "") {
      setIsNewPassError(true);
      return;
    }

    if (isConfirmPassError || isCurrPassError || isNewPassError) {
      return;
    }
    setIsSaveBtnDisabled(true);
    axios
      .put(`/account/users/password`, {
        Password: currPassword,
        NewPassword: newPassword,
      })
      .then((response) => {
        resetForm();
        if (response.data == null) {
          return;
        }
        alert(response.data);

        if (response.data === "You have successfully changed your password!") {
          onClose();
        }
      })
      .catch((err) => {
        alert(err.response.data);
        setIsCurrPassError(true);
      })
      .finally(() => {
        setIsSaveBtnDisabled(false);
      });
  };

  const handleCurrPassOnChange = (e) => {
    const input = e.target.value;
    if (input.trim() !== "") {
      setIsCurrPassError(false);
    }

    setCurrPassword(input);
  };

  const handleNewPassOnChange = (e) => {
    const input = e.target.value;
    setNewPassword(input);

    if (input.trim() === "") {
      setIsNewPassError(true);
    } else {
      setIsNewPassError(false);
    }
  };

  const handleConfirmPassOnChange = (e) => {
    setConfirmNewPassword(e.target.value);
    setIsConfirmPassError(newPassword !== e.target.value);
  };

  const handleOnClose = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setCurrPassword("");
    setConfirmNewPassword("");
    setNewPassword("");

    setIsConfirmPassError(false);
    setIsNewPassError(false);
    setIsCurrPassError(false);
  };

  return (
    <Dialog open={open} onClose={handleOnClose} maxWidth="sm">
      <DialogTitle
        variant="h4"
        className="title-header"
        sx={{
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
        }}
      >
        Change Password
      </DialogTitle>
      <DialogContent style={{ padding: "10px 24px" }}>
        <form onSubmit={handleChangePasswordSubmit}>
          <TextField
            label="Current Password"
            variant="outlined"
            fullWidth
            margin="normal"
            value={currPassword}
            onChange={handleCurrPassOnChange}
            type="password"
            className="profile-form-input"
            error={isCurrPassError}
            helperText={
              isCurrPassError ? "Wrong Password. Please, try again" : ""
            }
          />
          <TextField
            label="New Password"
            variant="outlined"
            fullWidth
            margin="normal"
            value={newPassword}
            onChange={handleNewPassOnChange}
            type="password"
            className="profile-form-input"
            error={isNewPassError}
            helperText={isNewPassError ? "Password is blank or invalid." : ""}
          />
          <TextField
            label="Confirm New Password"
            variant="outlined"
            fullWidth
            margin="normal"
            value={confirmNewPassword}
            onChange={handleConfirmPassOnChange}
            type="password"
            className="profile-form-input"
            error={isConfirmPassError}
            helperText={isConfirmPassError ? "Passwords do not match" : ""}
          />
          <Button
            disabled={isSaveBtnDisabled}
            type="submit"
            className="btn-primary profile-form-input"
            style={{ float: "right" }}
          >
            Save
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ForgotPasswordDialog = ({ open, onClose }) => {
  const { showAlert } = useShepDialogs();
  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(false);

  const handleForgetPassword = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailRegex = emailRegex.test(email);
    setIsEmailValid(isEmailRegex);

    if (isEmailRegex) {
      setIsSaveBtnDisabled(true);
      axios
        .post("/login/users/reset", email)
        .then((response) => {
          if (response.data == null) {
            return;
          }
          showAlert(response.data, { title: "Success", variant: "info" });
          setIsEmailValid(true);
          setEmail("");
        })
        .catch((err) => {
          showAlert("An error occurred.");
          setIsEmailValid(true);
          setEmail("");
        })
        .finally(() => {
          setIsSaveBtnDisabled(false);
          onClose()
        });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <DialogTitle
        variant="h4"
        className="title-header"
        sx={{
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
        }}
      >
        Forgot Password
      </DialogTitle>
      <DialogContent style={{ padding: "10px 24px" }}>
        <TextField
          label="Email Address"
          variant="outlined"
          fullWidth
          margin="normal"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="profile-form-input"
          error={!isEmailValid}
          helperText={!isEmailValid ? "Invalid email address" : ""}
        />
        <Button
          disabled={isSaveBtnDisabled}
          onClick={handleForgetPassword}
          className="btn-primary profile-form-input"
          style={{ float: "right" }}
        >
          Submit
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const ChangePolicyDialog = ({ open, onClose }) => {
  const [passwordPolicy, setPasswordPolicy] = useState(null);
  const [formValues, setFormValues] = useState({
    minPasswordLength: "",
    passwordDuration: "",
    requireUpperCase: false,
    requireNumericalDigits: false,
    requireSpecialCharacters: false,
    requireTwoFactorAuth: false,
    enableSSO: false,
    enforceSSO: false,
  });

  useEffect(() => {
    axios
      .get("/account/password-policy")
      .then((response) => {
        if (response.data == null) {
          return;
        }
        setPasswordPolicy(response.data);

        setFormValues({
          minPasswordLength: response.data.minPasswordLength,
          passwordDuration: response.data.passwordDurationDays,
          requireUpperCase: response.data.passwordRequiresUpperLower,
          requireNumericalDigits: response.data.passwordRequiresNumbers,
          requireSpecialCharacters: response.data.passwordRequiresSpecial,
          requireTwoFactorAuth: response.data.enableUser2FA,
          enableSSO: response.data.ssoEnabled,
          enforceSSO: response.data.restrictToSSO,
        });
      })
      .catch((err) => {
        alert(err.reponse.data);
      });
  }, []);

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]:
        name === "enableSSO" ||
          name === "enforceSSO" ||
          name === "requireUpperCase" ||
          name === "requireNumericalDigits" ||
          name === "requireSpecialCharacters" ||
          name === "requireTwoFactorAuth"
          ? checked
          : value,
    }));
  };

  const handleChangePolicySubmit = () => {
    const {
      minPasswordLength,
      passwordDuration,
      requireUpperCase,
      requireNumericalDigits,
      requireSpecialCharacters,
      requireTwoFactorAuth,
      enableSSO,
      enforceSSO,
    } = formValues;

    if (
      isNaN(formValues.minPasswordLength) ||
      formValues.minPasswordLength < 6 ||
      formValues.minPasswordLength > 32
    ) {
      return;
    } else if (
      isNaN(formValues.passwordDuration) ||
      formValues.passwordDuration < 1 ||
      formValues.passwordDuration > 365
    ) {
      return;
    }

    const updatedPasswordPolicy = {
      minPasswordLength: minPasswordLength,
      passwordDurationDays: passwordDuration,
      passwordRequiresUpperLower: requireUpperCase,
      passwordRequiresNumbers: requireNumericalDigits,
      passwordRequiresSpecial: requireSpecialCharacters,
      enableUser2FA: requireTwoFactorAuth,
      ssoEnabled: enableSSO,
      restrictToSSO: enforceSSO,
      enableERPAddressMap: passwordPolicy.enableERPAddressMap,
      enableCMS: passwordPolicy.enableCMS,
      autoPost830n862: passwordPolicy.autoPost830n862,
      autoPurgeLogAfterNMonths: passwordPolicy.autoPurgeLogAfterNMonths,
      roundRobinMaxDurationMinutes: passwordPolicy.roundRobinMaxDurationMinutes,
      automationWorkerCount: passwordPolicy.automationWorkerCount,
    };

    axios
      .put(`/account/password-policy`, updatedPasswordPolicy)
      .then((response) => {
        if (response.data == null) {
          return;
        }
        alert(response.data);

        if (response.data === "You have successfully changed your password!") {
          onClose();
        }
      })
      .catch((err) => {
        console.log(err);
        alert(err.response.data);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle
        variant="h4"
        sx={{
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
        }}
      >
        Change Password Policy
      </DialogTitle>
      <DialogContent style={{ padding: "24px" }}>
        {passwordPolicy == null ? (
          "Unable to load password Policy"
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleChangePolicySubmit();
            }}
          >
            <FormControl fullWidth>
              <Typography variant="body1" className="profile-form-input">
                Minimum Password Length (Min length: 6, Max length: 32)
              </Typography>
              <TextField
                hiddenLabel={true}
                id="change-policy-min-password-length"
                name="minPasswordLength"
                value={formValues.minPasswordLength ?? ""}
                onChange={handleChange}
                className="profile-form-input"
                fullWidth
                type="number"
                inputProps={{ min: 6, max: 32 }} // Set minimum and maximum values
                error={
                  isNaN(formValues.minPasswordLength) ||
                  formValues.minPasswordLength < 6 ||
                  formValues.minPasswordLength > 32
                }
                helperText={
                  isNaN(formValues.minPasswordLength) ||
                    formValues.minPasswordLength < 6 ||
                    formValues.minPasswordLength > 32
                    ? "Please enter a number between 6 and 32"
                    : ""
                }
              />
              <Typography variant="body1" className="profile-form-input">
                Password Duration Days (Min length: 1, Max length: 365)
              </Typography>
              <TextField
                hiddenLabel={true}
                id="change-policy-min-password-duration"
                name="passwordDuration"
                value={formValues.passwordDuration ?? ""}
                onChange={handleChange}
                className="profile-form-input"
                fullWidth
                type="number"
                inputProps={{ min: 1, max: 365 }}
                error={
                  isNaN(formValues.passwordDuration) ||
                  formValues.passwordDuration < 1 ||
                  formValues.passwordDuration > 365
                }
                helperText={
                  isNaN(formValues.passwordDuration) ||
                    formValues.passwordDuration < 1 ||
                    formValues.passwordDuration > 365
                    ? "Please enter a number between 1 and 365"
                    : ""
                }
              />
              <FormControlLabel
                control={<ShepCheckbox />}
                label="Require both (upper-case and lower-case letters)"
                name="requireUpperCase"
                checked={formValues.requireUpperCase}
                onChange={handleChange}
                className="profile-form-input"
              />
              <FormControlLabel
                control={<ShepCheckbox />}
                label="Require one or more numerical digits"
                name="requireNumericalDigits"
                checked={formValues.requireNumericalDigits}
                onChange={handleChange}
                className="profile-form-input"
              />
              <FormControlLabel
                control={<ShepCheckbox />}
                label="Require one or more special characters"
                name="requireSpecialCharacters"
                checked={formValues.requireSpecialCharacters}
                onChange={handleChange}
                className="profile-form-input"
              />
              <FormControlLabel
                control={<ShepCheckbox />}
                label="Require two Factor Authorization via Email"
                name="requireTwoFactorAuth"
                checked={formValues.requireTwoFactorAuth}
                onChange={handleChange}
                className="profile-form-input"
              />
              <Typography variant="h2" className="profile-form-input">
                SSO Options
              </Typography>
              <FormControlLabel
                control={<ShepCheckbox />}
                label="Enable Single sign on (SSO)"
                name="enableSSO"
                checked={formValues.enableSSO}
                onChange={handleChange}
                className="profile-form-input"
              />
              <FormControlLabel
                control={<ShepCheckbox />}
                label="Enforce Single sign on (SSO)"
                name="enforceSSO"
                checked={formValues.enforceSSO}
                onChange={handleChange}
                className="profile-form-input"
              />
            </FormControl>
            <Button
              type="submit"
              className="btn-primary profile-form-input"
              style={{ width: "80px", float: "right" }}
            >
              Save
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Profile = () => {
  dayjs.extend(utc);
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [isManageUsersOpen, setIsManageUsersOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isChangePassOpen, setIsChangePassOpen] = useState(false);
  const [isForgotPassOpen, setIsForgotPassOpen] = useState(false);
  const [isChangePolicyOpen, setIsChangePolicyOpen] = useState(false);
  const [loginHistory, setLoginHistory] = useState([]);
  const dispatch = useDispatch();

  const LogOut = () => {
    dispatch(logOut());

    // Heap.io: Record logout state

    window.heap.track("Profile - Log Out");

    window.heap.clearEventProperties();
    window.heap.addEventProperties({
      "Logged In": "false",
    });
  };

  useEffect(() => {
    axios
      .get(`/account/user-profile`)
      .then((response) => {
        setIsLoading(false);
        if (response.data == null) {
          return;
        }
        setUser(response.data.LeapFrogBetaUser);
        setRole(response.data.Role);
      })
      .catch((err) => console.log(err));

    axios
      .get(`/account/login-history`)
      .then((response) => {
        if (response.data == null) {
          return;
        }

        const rowsWithIds = response.data.map((row, idx) => ({
          ...row,
          LoginTime: new Date(row.LoginTime),
          id: idx,
        }));

        setLoginHistory(rowsWithIds);
      })
      .catch((err) => console.log(err.response.data));
  }, []);

  return (
    <Container>
      <MainNavigation selected={MenuItemEnum.Profile} />
      <Container className="content-container">
        <Card
          style={{
            height: "300px",
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              width: "100%",
            }}
          >
            <Button
              style={{ margin: "0px" }}
              className="btn-primary profile-btn"
              onClick={() => LogOut()}
            >
              Log Out
            </Button>
          </div>
          <div style={{ display: "flex" }}>
            {isLoading ? (
              "Loading..."
            ) : user != null || user === "" ? (
              <>
                <Avatar
                  alt="Profile Image"
                  src={`data:image/png;base64,${user.UserProfileImage}`}
                  sx={{ width: 100, height: 100, borderRadius: "50%" }}
                />
                <div className="profileLabels">
                  <Typography variant="h2">{user.UserName}</Typography>
                  <Typography variant="h2">
                    {user.Email ? `E-Mail: ${user.Email}` : ""}
                  </Typography>
                  <Typography variant="h2">
                    {role ? `Role: ${role}` : ""}
                  </Typography>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      marginTop: "60px",
                    }}
                  >
                    <ShepButton
                      variant="primary"
                      onClick={() => setIsChangePassOpen(true)}
                      sx={{ mx: "7px" }}
                    >
                      Change Password
                    </ShepButton>
                    <ChangePasswordDialog
                      open={isChangePassOpen}
                      onClose={() => setIsChangePassOpen(false)}
                    />
                    <ShepButton
                      variant="primary"
                      onClick={() => setIsForgotPassOpen(true)}
                      sx={{ mx: "7px" }}
                    >
                      Forgot Password
                    </ShepButton>
                    <ForgotPasswordDialog
                      open={isForgotPassOpen}
                      onClose={() => setIsForgotPassOpen(false)}
                    />
                    {(role === "admin" || role === "superadmin") && (
                      <>
                        <ShepButton
                          variant="primary"
                          onClick={() => setIsManageUsersOpen(true)}
                          sx={{ mx: "7px" }}
                        >
                          Manage Users
                        </ShepButton>
                        <ManageUsers
                          user={user}
                          open={isManageUsersOpen}
                          onClose={() => setIsManageUsersOpen(false)}
                        />
                        <ShepButton
                          variant="primary"
                          onClick={() => setIsChangePolicyOpen(true)}
                          sx={{ mx: "7px" }}
                        >
                          Account Settings
                        </ShepButton>
                        <ChangePolicyDialog
                          open={isChangePolicyOpen}
                          onClose={() => setIsChangePolicyOpen(false)}
                        />
                      </>
                    )}
                  </div>
                </div>
              </>
            ) : (
              "Unable to fetch user profile"
            )}
          </div>
        </Card>
        <Card>
          <Container
            sx={{
              height: "78px",
              backgroundColor: "var(--primary-main)",
              color: "var(--default-light)",
              padding: "20px 0px 20px 40px",
              borderRadius: "4px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              marginTop: "24px",
            }}
          >
            <Typography variant="h4" className="title-header">
              Login History
            </Typography>
          </Container>
          <PopUpDataGrid
            rows={loginHistory}
            columns={columns}
            whiteHeader={true}
            checkboxSelection={true}
            initialState={{
              sorting: {
                sortModel: [{ field: "LoginTime", sort: "desc" }],
              },
            }}
          />
        </Card>
      </Container>
    </Container>
  );
};

export default Profile;
